/**
 * Labstep
 *
 * @module screens/ProtocolCollection/Index
 * @desc ProtocolCollection index
 */

import { ProtocolCollectionActionCreateMenu } from 'labstep-web/components/ProtocolCollection/Action/CreateMenu';
import { ProtocolCollectionEmptyState } from 'labstep-web/components/ProtocolCollection/EmptyState';
import { ProtocolCollectionListOrTable } from 'labstep-web/components/ProtocolCollection/ListOrTable';
import { FolderType } from 'labstep-web/models/folder.model';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import { MasterIndex } from 'labstep-web/screens/Master/Index';
import { ProtocolCollectionIndexBreadcrumb } from 'labstep-web/screens/ProtocolCollection/Index/Breadcrumb';
import { ProtocolCollectionIndexLeftPane } from 'labstep-web/screens/ProtocolCollection/Index/LeftPane';
import { selectFolderIncludeSubfolders } from 'labstep-web/state/new/user-settings';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { bulkActions, filters } from './constants';
import type { IProtocolCollectionIndexProps } from './types';

export const ProtocolCollectionIndex: React.FC<
  IProtocolCollectionIndexProps
> = ({ group }) => {
  const includeSubfolders = useSelector(
    selectFolderIncludeSubfolders(FolderType.protocol_collection),
  );
  const breadcrumb = useMemo(
    () => <ProtocolCollectionIndexBreadcrumb />,
    [],
  );
  const leftPane = useMemo(
    () => <ProtocolCollectionIndexLeftPane />,
    [],
  );
  const action = useCallback(({ searchParams }: any) => {
    const folderId = searchParams?.folder_id;
    return (
      <ProtocolCollectionActionCreateMenu
        body={folderId ? { folder_id: folderId } : undefined}
      />
    );
  }, []);
  const noResultsMessage = useCallback(
    ({ searchParams }: any) => (
      <ProtocolCollectionEmptyState
        action={action({ searchParams })}
      />
    ),
    [],
  );
  const params = useMemo(
    () => ({
      group_id: group.id,
      include_subfolders: includeSubfolders,
    }),
    [group.id, includeSubfolders],
  );
  return (
    <MasterIndex
      entityName={ProtocolCollection.entityName}
      breadcrumb={breadcrumb}
      leftPane={leftPane}
      noResultsMessage={noResultsMessage}
      filters={filters}
      params={params}
      action={action}
      bulkActions={bulkActions}
    >
      {({
        entities,
        ...rest
      }: {
        entities: ProtocolCollection[];
      }): React.ReactElement => (
        <ProtocolCollectionListOrTable
          protocolCollections={entities}
          {...rest}
        />
      )}
    </MasterIndex>
  );
};
